import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Card, CardBody, Spinner } from '@components/ui';
import { TEST_IDS } from '@lib/utils/testIds';
import {
  DocumentTextIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowTrendingUpIcon,
  NoSymbolIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import {
  useDashboardMetrics,
  useDashboardCharts,
  useDashboardRankings,
  useDashboardStatusDistribution,
} from '../hooks/useDashboard';
import { useAuthStore } from '@features/auth/store/authStore';
import { formatNumber } from '@/utils/format';
import { useSelectedCompany } from '@features/company/hooks/useSelectedCompany';
import { companyService } from '@features/companies/services/companyService';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { MonthlyChartData } from '../types/dashboard.types';
import { canAccessDashboard } from '@lib/utils/roleNavigation';
import { BannerSlot } from '@features/announcements/components/BannerSlot';
import { DashboardKpiCard } from './DashboardKpiCard';

/**
 * Dashboard — Claude Design · Dashboard.dc.html
 *
 * QUE SE DIBUJA Y DE DONDE SALE — todo de la API, nada generado:
 *
 *   - Total Facturas / Ingresos del Período / Promedio por Factura: metrics
 *     (total_invoices, monthly_revenue, average_invoice), con sus mini-
 *     gráficos de la serie mensual de /dashboard/charts (MonthlyChartData).
 *   - Facturas Procesadas / con Error / Autorizado uso de la FE / Anuladas:
 *     NO salen de `metrics` (esos campos son approved/rejected/pending_invoices,
 *     la aprobación de NEGOCIO — un concepto distinto y confirmado que NO
 *     coincide con el estado real ante la DGI). Salen del mismo conteo real
 *     de eInvoiceStatus que arma "Distribución por estado" (mismo endpoint
 *     que "Facturas Activas"), agrupado según STATUS_GROUP_BY_KEY. Por eso
 *     no tienen % "vs período anterior" ni mini-gráfico: son un total del
 *     período, sin serie mensual propia — inventar una tendencia sería peor
 *     que no mostrar ninguna.
 *   - Aprobadas vs Rechazadas (mini-gráfico junto a Promedio por Factura):
 *     sí sale de metrics/monthlyData (approved/rejected mensual) — es la
 *     métrica de negocio, no la de eInvoiceStatus, y se rotula así a propósito.
 *   - Desglose por tipo de documento: rankings top_documents.
 *   - Selector Monto/Cantidad del grafico mensual: amount e invoices.
 *
 * QUITADO A PEDIDO (no por falta de dato — la API los sigue exponiendo, solo
 * ya no se muestran en esta pantalla): Clientes Activos, Tasa de aprobación,
 * Tiempo promedio de aprobación, Facturación proyectada, Top 5 Clientes y
 * Top 5 Tipos de Documento (la tabla; el desglose de docTypeBreakdown para
 * el KPI "Tipos de Documentos" se conserva, sale del mismo ranking).
 *
 * OMITIDO POR FALTA DE DATO. El diseño dibuja estas cosas y la API no las
 * expone; mostrarlas obligaria a inventar numeros:
 *
 *   - Motivos de rechazo (RUC invalido / Fuera de plazo).
 *   - Rango min / mediana / max de Promedio por factura → se muestra la
 *     tendencia mensual, que sí existe.
 */

// Panama usa el Balboa. El diseño lo pide explicito; antes se mostraba "$".
const CURRENCY = 'B/.';

/**
 * Agrupación de "Distribución por estado" en 4 categorías (descripción,
 * código de estado y color exactos, segunda corrección):
 *
 *   Procesada                = Procesada (0)                                              → verde/teal
 *   Autorizado uso de la FE  = Autorizado uso de la FE (2) + Procesando (3) + Pendiente (4) → amarillo
 *   Error                    = Error (1, el código puede variar)                            → rojo
 *   Anulada                  = Anulada (5)                                                   → gris
 *
 * Procesando y Pendiente van sumados dentro de "Autorizado uso de la FE",
 * NO dentro de Procesada (que ahora es solo el código 0 puro).
 *
 * Las claves son las etiquetas que produce `useDashboardStatusDistribution`
 * (INVOICE_STATUS_DISTRIBUTION). Cualquier estado que Aludra agregue y no
 * esté mapeado cae en "Otros", igual que antes.
 */
const STATUS_GROUP_BY_KEY: Record<string, string> = {
  'procesada': 'Procesada',
  'autorizado uso de la fe': 'Autorizado uso de la FE',
  'procesando': 'Autorizado uso de la FE',
  'pendiente': 'Autorizado uso de la FE',
  'error': 'Error',
  'anulada': 'Anulada',
};
const STATUS_GROUP_ORDER = ['Procesada', 'Autorizado uso de la FE', 'Error', 'Anulada', 'Otros'];
const STATUS_GROUP_COLOR: Record<string, string> = {
  'Procesada': '#12c4b2',
  'Autorizado uso de la FE': '#fada51',
  'Error': '#b8503a',
  'Anulada': '#94a3b8',
  'Otros': '#6b239e',
};
/** Paleta del desglose por tipo de documento. */
const DOC_TYPE_COLORS = ['#0a2233', '#6b239e', '#12c4b2', '#f5991d', '#94a3b8'];

const CARD_SHELL =
  'rounded-[14px] border border-[#ececec] bg-white shadow-[0_1px_2px_rgba(10,34,51,.03),0_8px_20px_-10px_rgba(10,34,51,.10)] dark:border-secondary-700 dark:bg-secondary-800';

/** Cabecera reutilizada por las tarjetas grandes (graficos y tablas). */
function SectionHead({
  title,
  subtitle,
  right,
}: {
  title: string;
  subtitle: string;
  right?: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="text-sm font-bold tracking-[-0.015em] text-ink-850 dark:text-white">{title}</div>
        <div className="mt-1 text-xs text-secondary-400">{subtitle}</div>
      </div>
      {right}
    </div>
  );
}

/**
 * Donut para desgloses categóricos (Distribución por estado, Tipos de
 * Documentos) — la usan las dos tarjetas de "Gráficos" para que ocupen el
 * mismo espacio con el mismo lenguaje visual (contenedor a 184px de alto).
 *
 * Con categorías tan desparejas como 95%/5%/0.02%/0.04%, Recharts le calcula
 * mal el ángulo a una porción casi nula y el arco sale roto. `pieData` le
 * pone un piso del 1% del total SOLO para el dibujo — la leyenda, el
 * tooltip y el total del centro siguen usando los valores reales de `data`.
 * Probado con esos números exactos antes de dejarlo así.
 */
function BreakdownDonutChart({
  data,
  valueLabel,
  centerLabel,
}: {
  data: Array<{ name: string; value: number; color: string }>;
  valueLabel: string;
  centerLabel: string;
}) {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const floor = total > 0 ? total * 0.01 : 0;
  const pieData = data.map((d) => ({ ...d, value: Math.max(d.value, floor) }));

  // El numero del centro se achica segun cuantos digitos tiene: a font fijo,
  // un total de 6+ cifras ("263,207") se salia del agujero del donut y el
  // texto quedaba superpuesto sobre el anillo en vez de adentro.
  const totalStr = formatNumber(total, 0);
  const centerTextSize =
    totalStr.length > 9 ? 'text-[13px]' : totalStr.length > 6 ? 'text-sm' : totalStr.length > 4 ? 'text-base' : 'text-lg';

  return (
    <div className="flex h-[184px] flex-wrap items-center gap-4">
      {/*
        PieChart con medidas fijas en vez de ResponsiveContainer: dentro de
        un flex item el contenedor responsivo medía 0 de alto y el donut
        salía como una línea.

        cx/cy=80 y outerRadius=72 en un lienzo de 160: eso deja 8px de
        margen entre el borde del círculo (80+72=152) y el borde del SVG
        (160) — antes cx/outerRadius sumaban EXACTO el ancho del lienzo
        (78+78=156 de 156), sin ningún margen, y el anti-aliasing del borde
        se recortaba contra el borde del SVG (se veía cortado a la derecha
        y hacia abajo, que es hacia donde crecen x e y).
      */}
      <div className="relative h-[160px] w-[160px] shrink-0">
        <PieChart width={160} height={160}>
          <Pie
            data={pieData}
            cx={80}
            cy={80}
            innerRadius={50}
            outerRadius={72}
            paddingAngle={2}
            dataKey="value"
            stroke="none"
            isAnimationActive={false}
          >
            {pieData.map((d, i) => (
              <Cell key={i} fill={d.color} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              backgroundColor: '#ffffff',
              border: '1px solid #ececec',
              borderRadius: '10px',
              fontSize: 12,
            }}
            // El value que llega acá es el de pieData (con piso visual en las
            // porciones casi nulas) — se busca el real en `data` por nombre
            // para no mostrar el número inflado en el tooltip. El nombre
            // (2do elemento) antes devolvía `valueLabel` fijo ("Documentos"
            // para las dos tarjetas) en vez del nombre real de la porción
            // (p.ej. "Nota de Crédito") — por eso no se veía qué categoría
            // era al pasar el mouse.
            formatter={(_value: any, name: any) => {
              const real = data.find((d) => d.name === name)?.value ?? 0;
              const pct = total > 0 ? ((real / total) * 100).toFixed(1) : '0.0';
              return [`${formatNumber(real, 0)} (${pct}%) · ${valueLabel}`, name];
            }}
          />
        </PieChart>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-2">
          <span className={`${centerTextSize} font-extrabold leading-tight tracking-[-0.02em] text-ink-850 dark:text-white`}>
            {totalStr}
          </span>
          <span className="text-[10px] font-semibold tracking-[0.14em] text-secondary-400">
            {centerLabel}
          </span>
        </div>
      </div>
      <div className="flex min-w-[140px] flex-1 flex-col gap-2.5">
        {data.map((d) => (
          <div key={d.name} className="flex items-center justify-between gap-3">
            {/*
              flex-1 explícito en el nombre (antes no lo tenía — dependía del
              auto-sizing del flex item, que en algunos anchos de tarjeta lo
              dejaba angosto de más y el truncate lo recortaba mucho). Ahora
              el nombre se come todo el espacio que sobra y el numero/% van
              en una columna angosta fija a la derecha, que nunca se achica.
            */}
            <span className="inline-flex min-w-0 flex-1 items-center gap-1.5 text-xs text-secondary-500" title={d.name}>
              <span className="h-2 w-2 shrink-0 rounded-sm" style={{ background: d.color }} />
              <span className="truncate">{d.name}</span>
            </span>
            <span className="shrink-0 text-right">
              <div className="font-mono text-xs font-semibold text-ink-850 dark:text-white">
                {formatNumber(d.value, 0)}
              </div>
              <div className="font-mono text-[11px] text-secondary-400">
                {total > 0 ? `${((d.value / total) * 100).toFixed(1)}%` : '—'}
              </div>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Mini-tendencia de un KPI (linea o barras), con tooltip real al pasar el
 * mouse — las versiones anteriores (Sparkline/MiniBars de MiniCharts.tsx)
 * son SVG a mano sin ninguna interaccion. Estas usan Recharts de verdad, así
 * que el tooltip sale gratis. No se tocó MiniCharts.tsx: esos componentes
 * los siguen usando otras pantallas (Clientes, Productos, Archivo Histórico)
 * y no hace falta cambiarles el comportamiento.
 */
function MiniTrendChart({
  data,
  dataKey,
  color,
  mutedColor = '#d7c2ea',
  type = 'line',
  valueLabel,
  formatValue,
}: {
  data: Array<Record<string, string | number>>;
  dataKey: string;
  color: string;
  mutedColor?: string;
  type?: 'line' | 'bar';
  valueLabel: string;
  formatValue?: (v: number) => string;
}) {
  if (data.length < 2) return null;
  const fmt = formatValue ?? ((v: number) => formatNumber(v, 0));
  return (
    <div className="h-8">
      <ResponsiveContainer width="100%" height="100%">
        {type === 'bar' ? (
          <BarChart data={data} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
            <XAxis dataKey="month" hide />
            <Tooltip
              cursor={{ fill: 'rgba(10,34,51,.06)' }}
              contentStyle={{
                backgroundColor: '#ffffff',
                border: '1px solid #ececec',
                borderRadius: '8px',
                fontSize: 11,
                padding: '4px 8px',
              }}
              labelStyle={{ fontWeight: 700, color: '#0a2233', marginBottom: 2 }}
              formatter={(value: any) => [fmt(Number(value)), valueLabel]}
            />
            <Bar dataKey={dataKey} radius={[3, 3, 0, 0]} isAnimationActive={false}>
              {data.map((_, i) => (
                <Cell key={i} fill={i === data.length - 1 ? color : mutedColor} />
              ))}
            </Bar>
          </BarChart>
        ) : (
          <LineChart data={data} margin={{ top: 2, right: 2, left: 2, bottom: 0 }}>
            <XAxis dataKey="month" hide />
            <Tooltip
              contentStyle={{
                backgroundColor: '#ffffff',
                border: '1px solid #ececec',
                borderRadius: '8px',
                fontSize: 11,
                padding: '4px 8px',
              }}
              labelStyle={{ fontWeight: 700, color: '#0a2233', marginBottom: 2 }}
              formatter={(value: any) => [fmt(Number(value)), valueLabel]}
            />
            <Line
              type="monotone"
              dataKey={dataKey}
              stroke={color}
              strokeWidth={1.5}
              dot={false}
              activeDot={{ r: 3 }}
              isAnimationActive={false}
            />
          </LineChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

/** Como MiniTrendChart, pero con dos series (Aprobadas vs Rechazadas). */
function MiniDualTrendChart({
  data,
  dataKeyA,
  dataKeyB,
  nameA,
  nameB,
  colorA,
  colorB,
}: {
  data: Array<Record<string, string | number>>;
  dataKeyA: string;
  dataKeyB: string;
  nameA: string;
  nameB: string;
  colorA: string;
  colorB: string;
}) {
  if (data.length < 2) return null;
  return (
    <div className="h-8">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 2, right: 2, left: 2, bottom: 0 }}>
          <XAxis dataKey="month" hide />
          <Tooltip
            contentStyle={{
              backgroundColor: '#ffffff',
              border: '1px solid #ececec',
              borderRadius: '8px',
              fontSize: 11,
              padding: '4px 8px',
            }}
            labelStyle={{ fontWeight: 700, color: '#0a2233', marginBottom: 2 }}
            formatter={(value: any) => formatNumber(Number(value), 0)}
          />
          <Line
            type="monotone"
            dataKey={dataKeyA}
            name={nameA}
            stroke={colorA}
            strokeWidth={1.5}
            dot={false}
            activeDot={{ r: 3 }}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey={dataKeyB}
            name={nameB}
            stroke={colorB}
            strokeWidth={1.5}
            dot={false}
            activeDot={{ r: 3 }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  // Redirect restricted roles to Facturas Activas
  useEffect(() => {
    if (user?.role && !canAccessDashboard(user.role)) {
      navigate('/home/main', { replace: true });
    }
  }, [user?.role, navigate]);

  // Calcular el primer día del mes actual
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [startDate, setStartDate] = useState(firstDayOfMonth.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);

  /** Metrica del grafico mensual. El backend devuelve ambas en monthlyData. */
  const [monthlyMetric, setMonthlyMetric] = useState<'amount' | 'invoices'>('invoices');

  const { data: selectedCompanyData, isLoading: companyLoading, refetch: refetchSelectedCompany } = useSelectedCompany();
  const companyId = selectedCompanyData?.company?.id;
  const hasCompany = !!companyId;
  const [autoSelecting, setAutoSelecting] = useState(false);

  // Auto-select first company if none is selected (skip for super admin — they use CompanySelectorPage)
  const isSuperAdmin = (user as any)?.isSuperAdmin === true;
  useEffect(() => {
    if (!hasCompany && !companyLoading && !autoSelecting && !isSuperAdmin) {
      setAutoSelecting(true);
      companyService.getCompanies()
        .then((data) => {
          const companies = data?.companies || [];
          if (companies.length > 0) {
            return companyService.selectCompany(companies[0].id);
          }
        })
        .then(() => refetchSelectedCompany())
        .catch((err) => console.error('Auto-select company failed:', err))
        .finally(() => setAutoSelecting(false));
    }
  }, [hasCompany, companyLoading, autoSelecting, isSuperAdmin, refetchSelectedCompany]);

  const { data: metricsResponse, isLoading, error: metricsError } = useDashboardMetrics(startDate, endDate, hasCompany);
  const { data: chartsResponse, isLoading: chartsLoading, error: chartsError } = useDashboardCharts(6, hasCompany);
  const { data: rankingsResponse, isLoading: rankingsLoading, error: rankingsError } = useDashboardRankings(5, hasCompany);

  // Si CUALQUIERA de los tres falla (p.ej. 502 de Aludra para un tenant
  // puntual), antes se reemplazaba TODO el dashboard por una tarjeta de error
  // a pantalla completa — filtros, graficos y todo lo demas que sí había
  // cargado bien desaparecían con él. Ahora cada pieza ya sabe degradar sola
  // (metrics/monthlyData/topDocuments caen a vacío/0 más abajo cuando su
  // respuesta falta), así que solo se avisa con un aviso chico y no bloqueante
  // y se deja el resto de la pantalla utilizable.
  const hasLoadError = Boolean(metricsError || chartsError || rankingsError);

  const metrics = useMemo(() => {
    return metricsResponse?.data?.[0];
  }, [metricsResponse]);

  // Grafico mensual (barras). La distribucion por estado ya NO se toma de aqui:
  // Aludra devuelve buckets genericos (Pending/Approved/Rejected/Others) que no
  // concuerdan con los filtros de "Facturas Activas". Ver statusData abajo.
  const monthlyData = useMemo(() => {
    if (!chartsResponse?.data) {
      return [];
    }

    return chartsResponse.data.filter(
      (item): item is MonthlyChartData => item.dataset_type === 'monthly'
    ).sort((a, b) => {
      // Sort by year and month
      const yearDiff = parseInt(a.year) - parseInt(b.year);
      if (yearDiff !== 0) return yearDiff;
      return parseInt(a.month_num) - parseInt(b.month_num);
    });
  }, [chartsResponse]);

  // Distribucion por Estado: se construye a partir de las facturas reales (mismo
  // endpoint que "Facturas Activas"), agrupadas por eInvoiceStatus, usando el
  // MISMO rango de fechas que el resto del dashboard. Asi los estados y conteos
  // concuerdan con el listado cuando se usa el mismo periodo.
  // `total` del hook no se desestructura: la porcion "Otros" que agrega el propio
  // hook hace que la suma de las porciones ya cuadre con el total de la tabla,
  // y ese es el statusTotal que usa el centro del donut mas abajo.
  const { data: statusDistribution, isLoading: statusLoading } =
    useDashboardStatusDistribution(companyId, startDate, endDate, hasCompany);

  const statusData = useMemo(() => {
    // Agrupa los 6 estados crudos en las 4 categorías de STATUS_GROUP_BY_KEY.
    const totals: Record<string, number> = {};
    for (const entry of statusDistribution) {
      if (!entry.value || entry.value <= 0) continue;
      const group = STATUS_GROUP_BY_KEY[entry.name?.trim().toLowerCase()] ?? 'Otros';
      totals[group] = (totals[group] ?? 0) + entry.value;
    }
    return STATUS_GROUP_ORDER
      .filter((group) => (totals[group] ?? 0) > 0)
      .map((group) => ({ name: group, value: totals[group], color: STATUS_GROUP_COLOR[group] }));
  }, [statusDistribution]);

  // Top documentos — sigue haciendo falta para el desglose del KPI "Tipos de
  // Documentos" (docTypeBreakdown más abajo), aunque la tabla "Top 5 Tipos de
  // Documento" ya no se muestre. Top clientes se quitó junto con esa tabla.
  const { topDocuments } = useMemo(() => {
    if (!rankingsResponse?.data) {
      return { topDocuments: [] };
    }

    const documents = rankingsResponse.data
      .filter(item => item.ranking_type === 'top_documents')
      .sort((a, b) => a.rank_order - b.rank_order)
      .slice(0, 5);

    return { topDocuments: documents };
  }, [rankingsResponse]);

  /**
   * Series mensuales listas para los mini-graficos. Salen tal cual de
   * MonthlyChartData (los campos vienen como string desde Aludra).
   */
  const series = useMemo(() => {
    const num = (v: string | null) => Number(v ?? 0) || 0;
    return {
      invoices: monthlyData.map((m) => num(m.invoices)),
      amount: monthlyData.map((m) => num(m.amount)),
      pending: monthlyData.map((m) => num(m.pending)),
      rejected: monthlyData.map((m) => num(m.rejected)),
      // Mismo dataset mensual que ya se pedía (chartsResponse) — approved
      // venía sin usar. Alimenta el mini-grafico "Aprobadas vs Rechazadas"
      // de al lado de Tasa de aprobación, sin ninguna consulta nueva.
      approved: monthlyData.map((m) => num(m.approved)),
      // Promedio por factura de cada mes: monto / cantidad.
      average: monthlyData.map((m) => {
        const inv = num(m.invoices);
        return inv > 0 ? num(m.amount) / inv : 0;
      }),
      // Sin Array.at(): el target de TS del proyecto es anterior a ES2022.
      lastMonthLabel: monthlyData.length > 0 ? monthlyData[monthlyData.length - 1].month : '',
    };
  }, [monthlyData]);

  /**
   * Mismos valores de `series`, pero con el mes de cada punto adjunto — lo
   * necesitan los mini-gráficos de los KPIs (MiniTrendChart/MiniDualTrendChart)
   * para poder mostrar "Mar: 245" al pasar el mouse en vez de un numero suelto
   * sin contexto.
   */
  const chartSeries = useMemo(
    () =>
      monthlyData.map((m, i) => ({
        month: m.month,
        invoices: series.invoices[i],
        amount: series.amount[i],
        pending: series.pending[i],
        rejected: series.rejected[i],
        approved: series.approved[i],
        average: series.average[i],
      })),
    [monthlyData, series]
  );

  /** Total de documentos — suma real de las porciones agrupadas. */
  const statusTotal = useMemo(
    () => statusData.reduce((sum, s) => sum + (s.value ?? 0), 0),
    [statusData]
  );

  /** Facturas anuladas del período — mismo bucket que usa el KPI "Facturas Anuladas". */
  const voidedInvoices = useMemo(
    () => statusData.find((s) => s.name === 'Anulada')?.value ?? 0,
    [statusData]
  );

  /**
   * "Facturas Procesadas" / "Facturas con Error" / "Facturas Autorizado uso
   * de la FE" mostraban `metrics.approved_invoices` / `rejected_invoices` /
   * `pending_invoices` — la aprobación de NEGOCIO, un dato totalmente
   * distinto al conteo real de eInvoiceStatus que arma "Distribución por
   * estado" (mismo endpoint que "Facturas Activas"). Confirmado con números
   * reales que no coinciden. Ahora las tres tarjetas sacan el valor del
   * MISMO bucket que ya usa la distribución de abajo — sin consulta nueva,
   * y sin riesgo de volver a mostrar un número que no es el real.
   */
  const processedInvoices = useMemo(
    () => statusData.find((s) => s.name === 'Procesada')?.value ?? 0,
    [statusData]
  );
  const errorInvoices = useMemo(
    () => statusData.find((s) => s.name === 'Error')?.value ?? 0,
    [statusData]
  );
  const authorizedInvoices = useMemo(
    () => statusData.find((s) => s.name === 'Autorizado uso de la FE')?.value ?? 0,
    [statusData]
  );

  /** Desglose por tipo de documento del KPI 07 — sale de rankings, no inventado. */
  const docTypeBreakdown = useMemo(
    () =>
      topDocuments.map((doc, i) => ({
        // Fallback defensivo: si /dashboard/rankings devuelve `name` vacío
        // para algún tipo de documento (visto reportado como "no muestra el
        // nombre"), que se vea el identificador o "Sin nombre" en vez de un
        // renglón en blanco — así se nota que es un dato faltante del
        // backend y no un bug de layout.
        name: doc.name?.trim() || doc.identifier || 'Sin nombre',
        quantity: doc.quantity,
        color: DOC_TYPE_COLORS[i % DOC_TYPE_COLORS.length],
      })),
    [topDocuments]
  );

  const iconCls = 'h-3.5 w-3.5';

  /**
   * Fallback a 0 para los campos de `metrics` que arman las tarjetas 06 y 07
   * (Ingresos del Período / Promedio por Factura) — si /dashboard/metrics
   * falla (metricsError) o vuelve vacío, `metrics` es undefined. Antes eso
   * hacía `return []` y las 8 tarjetas de KPIs (más los dos gráficos de abajo)
   * desaparecían detrás de la tarjeta "No hay datos disponibles", tapando
   * incluso lo que sí había cargado bien (statusData, docTypeBreakdown). Ahora
   * las tarjetas siempre se arman — con 0 en vez de dato — y el aviso de
   * `hasLoadError` de arriba ya avisa que algo no cargó.
   */
  const metricsSafe = useMemo(
    () => ({
      monthly_revenue: metrics?.monthly_revenue ?? 0,
      monthly_revenue_change_pct: metrics?.monthly_revenue_change_pct ?? 0,
      average_invoice: metrics?.average_invoice ?? 0,
      average_invoice_change_pct: metrics?.average_invoice_change_pct ?? 0,
    }),
    [metrics]
  );

  const stats = useMemo(() => {
    return [
      {
        index: '01',
        // CORREGIDO: mostraba metrics.total_invoices (endpoint de negocio),
        // que el usuario confirmó que NO da el total real — el real (24,363)
        // es exactamente la suma de los 4 buckets de eInvoiceStatus
        // (statusTotal, mismo dato que "Distribución por estado"). Mismo
        // caso que Procesadas/Error/Autorizado/Anuladas: se cambia la fuente
        // al conteo real, y por eso se cae el % vs período anterior y el
        // mini-gráfico (metrics.total_invoices_change_pct y la serie mensual
        // de invoices son del endpoint de negocio — no hay forma honesta de
        // mostrar una tendencia del dato correcto sin inventarla).
        name: 'Total Facturas',
        value: statusLoading ? '—' : formatNumber(statusTotal, 0),
        icon: <DocumentTextIcon className={iconCls} />,
        iconTone: 'neutral' as const,
        description: 'del período',
        testId: TEST_IDS.DASHBOARD.STATS.TOTAL_INVOICES,
      },
      {
        // CORREGIDO: mostraba metrics.approved_invoices (aprobación de
        // NEGOCIO), un dato distinto al conteo real de eInvoiceStatus
        // "Procesada" — confirmado con números reales que no coincidían.
        // Ahora sale del mismo bucket que usa "Distribución por estado" de
        // abajo (mismo dato, sin consulta nueva). Sin % vs período anterior
        // ni mini-gráfico: no hay serie mensual para este bucket agrupado,
        // solo el total del período — mostrar una tendencia inventada sería
        // peor que no mostrar ninguna.
        index: '02',
        name: 'Facturas Procesadas',
        value: statusLoading ? '—' : formatNumber(processedInvoices, 0),
        icon: <CheckCircleIcon className={iconCls} />,
        iconTone: 'teal' as const,
        description: 'del período',
        testId: TEST_IDS.DASHBOARD.STATS.APPROVED_INVOICES,
      },
      {
        // CORREGIDO: mismo caso — mostraba metrics.rejected_invoices en vez
        // del conteo real de eInvoiceStatus "Error".
        index: '03',
        name: 'Facturas con Error',
        value: statusLoading ? '—' : formatNumber(errorInvoices, 0),
        icon: <XCircleIcon className={iconCls} />,
        iconTone: 'red' as const,
        description: 'del período',
        testId: TEST_IDS.DASHBOARD.STATS.REJECTED_INVOICES,
      },
      {
        // CORREGIDO: mismo caso — mostraba metrics.pending_invoices en vez
        // del conteo real de "Autorizado uso de la FE" (códigos 2, 3 y 4).
        index: '04',
        name: 'Facturas Autorizado uso de la FE',
        value: statusLoading ? '—' : formatNumber(authorizedInvoices, 0),
        icon: <ClockIcon className={iconCls} />,
        iconTone: 'amber' as const,
        description: 'del período',
        testId: TEST_IDS.DASHBOARD.STATS.PENDING_INVOICES,
      },
      {
        index: '05',
        name: 'Facturas Anuladas',
        // Mismo dato que ya trae "Distribución por estado" (statusData) — no
        // es una consulta nueva, solo se reusa el bucket "Anulada".
        value: statusLoading ? '—' : formatNumber(voidedInvoices, 0),
        icon: <NoSymbolIcon className={iconCls} />,
        iconTone: 'neutral' as const,
        description: 'del período',
        testId: TEST_IDS.DASHBOARD.STATS.VOIDED_INVOICES,
      },
      {
        index: '06',
        name: 'Ingresos del Período',
        value: `${CURRENCY} ${formatNumber(metricsSafe.monthly_revenue)}`,
        icon: <CurrencyDollarIcon className={iconCls} />,
        iconTone: 'teal' as const,
        change: `${metricsSafe.monthly_revenue_change_pct > 0 ? '+' : ''}${formatNumber(metricsSafe.monthly_revenue_change_pct, 2)}%`,
        changeType: metricsSafe.monthly_revenue_change_pct >= 0 ? 'positive' as const : 'negative' as const,
        description: 'vs período anterior',
        testId: TEST_IDS.DASHBOARD.STATS.REVENUE,
        footer: (
          <MiniTrendChart
            data={chartSeries}
            dataKey="amount"
            color="#0d9488"
            type="bar"
            valueLabel="Monto"
            formatValue={(v) => `${CURRENCY} ${formatNumber(v)}`}
          />
        ),
      },
      {
        index: '07',
        name: 'Promedio por Factura',
        value: `${CURRENCY} ${formatNumber(metricsSafe.average_invoice)}`,
        icon: <ArrowTrendingUpIcon className={iconCls} />,
        iconTone: 'neutral' as const,
        change: `${metricsSafe.average_invoice_change_pct > 0 ? '+' : ''}${formatNumber(metricsSafe.average_invoice_change_pct, 2)}%`,
        changeType: metricsSafe.average_invoice_change_pct >= 0 ? 'positive' as const : 'negative' as const,
        description: 'vs período anterior',
        testId: TEST_IDS.DASHBOARD.STATS.AVERAGE_INVOICE,
        footer: (
          <MiniTrendChart
            data={chartSeries}
            dataKey="average"
            color="#6b239e"
            valueLabel="Promedio"
            formatValue={(v) => `${CURRENCY} ${formatNumber(v)}`}
          />
        ),
      },
    ];
  }, [metricsSafe, chartSeries, statusTotal, voidedInvoices, processedInvoices, errorInvoices, authorizedInvoices, statusLoading]);

  /** Cabecera de pagina — se repite en todos los estados (carga, error, vacio). */
  const pageHead = (
    <div>
      {/*
        El ultimo segmento del breadcrumb ES el titulo (h1) — no un h1 suelto
        sin breadcrumb, y sin eyebrow arriba (quedaba de mas junto al
        breadcrumb, que ya da el mismo contexto). "Inicio" y "/" se quedan al
        tamaño de breadcrumb; "Dashboard" sube de tamaño pero no al de un
        display — es un titulo de pantalla, no una portada.
      */}
      <nav className="flex items-baseline gap-2">
        <span className="text-[14px] text-secondary-400">Inicio</span>
        <span className="text-[14px] text-secondary-300">/</span>
        <h1 className="text-2xl font-bold tracking-[-0.03em] text-ink-850 dark:text-white">Dashboard</h1>
      </nav>
      <p className="mt-2 text-[15px] text-secondary-500 dark:text-secondary-400">
        Resumen general de tu actividad de facturación electrónica.
      </p>
    </div>
  );

  // companyLoading = true only on first fetch (data is undefined)
  // Once data is null or an object, companyLoading = false
  if (companyLoading) {
    return (
      <div className="space-y-7 font-display">
        {pageHead}
        <div className="flex justify-center py-12">
          <Spinner className="w-12 h-12" />
        </div>
      </div>
    );
  }

  if (!hasCompany) {
    return (
      <div className="space-y-7 font-display">
        {pageHead}
        <Card>
          <CardBody className="p-12 text-center">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-secondary-400 mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
              No tienes compañías asignadas
            </h3>
            <p className="text-secondary-600 dark:text-secondary-400">
              Contacta a tu administrador para que te asigne una compañía.
            </p>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 font-display" data-testid={TEST_IDS.DASHBOARD.CONTAINER}>
      {pageHead}

      {/* Noticias, promociones y avisos — el contenido y a quién le sale se
          administran desde el JSON de banners, no desde acá. */}
      <BannerSlot />

      {/*
        Aviso chico y no bloqueante — reemplaza la tarjeta de error a pantalla
        completa de antes. El dashboard sigue abajo con lo que sí pudo cargar
        (gráficos/tarjetas vacíos donde falte dato), en vez de un callejón sin
        salida.
      */}
      {hasLoadError && (
        <Alert variant="warning" data-testid={TEST_IDS.DASHBOARD.ERROR_ALERT}>
          No pudimos cargar algunas métricas del dashboard en este momento. Mostrando la información disponible — intenta recargar en unos minutos.
        </Alert>
      )}

      {/* Filtros — label, fecha y botón en una sola fila (antes la label iba
          arriba del input, en dos líneas por campo). */}
      <div className={`${CARD_SHELL} mt-7 flex flex-wrap items-center gap-5 px-5 py-[18px]`}>
        <div className="flex items-center gap-2.5">
          <label
            htmlFor="dashboard-start-date"
            className="whitespace-nowrap text-[12px] font-bold uppercase tracking-[0.16em] text-secondary-500"
          >
            Fecha inicio
          </label>
          <input
            id="dashboard-start-date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            data-testid={TEST_IDS.DASHBOARD.START_DATE_INPUT}
            className="rounded-lg border border-[#ececec] bg-white px-3 py-2.5 text-[14px] font-medium text-ink-850 transition-colors hover:border-secondary-300 focus:border-secondary-400 focus:ring-4 focus:ring-secondary-400/[.14] dark:border-secondary-600 dark:bg-secondary-800 dark:text-white"
          />
        </div>
        <div className="flex items-center gap-2.5">
          <label
            htmlFor="dashboard-end-date"
            className="whitespace-nowrap text-[12px] font-bold uppercase tracking-[0.16em] text-secondary-500"
          >
            Fecha fin
          </label>
          <input
            id="dashboard-end-date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            data-testid={TEST_IDS.DASHBOARD.END_DATE_INPUT}
            className="rounded-lg border border-[#ececec] bg-white px-3 py-2.5 text-[14px] font-medium text-ink-850 transition-colors hover:border-secondary-300 focus:border-secondary-400 focus:ring-4 focus:ring-secondary-400/[.14] dark:border-secondary-600 dark:bg-secondary-800 dark:text-white"
          />
        </div>

        <button
          type="button"
          onClick={() => {
            // Trigger refetch by updating dates
            const newEndDate = new Date().toISOString().split('T')[0];
            setEndDate(newEndDate);
          }}
          disabled={isLoading}
          data-testid={TEST_IDS.DASHBOARD.UPDATE_BUTTON}
          className="cta-sheen ml-auto inline-flex items-center gap-2 rounded-lg bg-ink-850 px-[18px] py-2.5 text-[14px] font-semibold text-white transition-colors hover:bg-[#142d40] disabled:cursor-not-allowed disabled:opacity-60"
        >
          <ArrowPathIcon className="h-3.5 w-3.5" />
          Actualizar
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-24" data-testid={TEST_IDS.DASHBOARD.LOADING}>
          <Spinner className="w-12 h-12" />
        </div>
      ) : (
        <>
          {/* KPIs — 7 tarjetas de stats + Aprobadas vs Rechazadas como 8va,
              para llenar las 2 filas de 4. Tasa de aprobación se quitó a
              pedido.

              Ya NO se condiciona a `metrics` truthy: si /dashboard/metrics
              falla o vuelve vacío, `metrics` es undefined y esto se saltaba
              directo al bloque "No hay datos disponibles" de más abajo,
              tapando TODO — KPIs, gráficos y tablas que sí habían cargado
              bien. `stats` ya arma sus valores con `metricsSafe` (0 en vez de
              dato faltante) y cada sección de abajo ya tenía su propio
              estado vacío ("Sin facturas en el período", etc.) — no hace
              falta una pantalla de error a nivel página, el aviso chico de
              `hasLoadError` ya cumple ese rol sin bloquear nada. */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {stats.map((stat) => (
              <DashboardKpiCard
                key={stat.name}
                index={stat.index}
                icon={stat.icon}
                iconTone={stat.iconTone}
                name={stat.name}
                value={stat.value}
                change={stat.change}
                changeType={stat.changeType}
                description={stat.description}
                footer={stat.footer}
                data-testid={stat.testId}
              />
            ))}

            {/* Aprobadas vs Rechazadas — con datos que ya se piden para los
                sparklines de arriba (chartsResponse.approved/rejected),
                sin ninguna consulta nueva. */}
            <div className={`${CARD_SHELL} p-5`}>
              <div className="text-xs font-bold uppercase tracking-[0.14em] text-secondary-500">
                Aprobadas vs Rechazadas
              </div>
              <div className="mt-2.5 flex items-center gap-3 text-[11px] text-secondary-500">
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-sm bg-[#0d9488]" /> Aprobadas
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-sm bg-[#b8503a]" /> Rechazadas
                </span>
              </div>
              <div className="mt-2">
                <MiniDualTrendChart
                  data={chartSeries}
                  dataKeyA="approved"
                  dataKeyB="rejected"
                  nameA="Aprobadas"
                  nameB="Rechazadas"
                  colorA="#0d9488"
                  colorB="#b8503a"
                />
              </div>
              <div className="mt-1 text-[11px] text-secondary-400">Últimos 6 meses</div>
            </div>
          </div>

          {/*
            Gráficos — Distribución por estado y Tipos de Documentos van
            50/50 en la misma fila; Facturación mensual debajo, a todo el
            ancho.

            CORREGIDO: las tres tarjetas dependían de la MISMA condición
            `!chartsLoading && monthlyData.length > 0` — pero Distribución
            por estado (useDashboardStatusDistribution) y Tipos de
            Documentos (rankings) tienen sus propias fuentes de datos,
            totalmente independientes de monthlyData. Si esa condición
            fallaba (chartsLoading atascado, o el rango de fechas sin datos
            en /dashboard/charts), las tres desaparecían juntas aunque las
            dos primeras tuvieran datos propios de sobra. Ahora cada una
            tiene su propio gate — cada tarjeta ya manejaba su estado de
            carga/vacío por separado, solo hacía falta no taparlas con la
            condición ajena.
          */}
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {/* Distribución por estado — donut con piso mínimo del 1%
                (ver BreakdownDonutChart) para que categorías casi en 0%
                no rompan el ángulo, y el mismo componente que Tipos de
                Documentos para que ocupen el mismo espacio. */}
            <div className={`${CARD_SHELL} p-6`} data-testid={TEST_IDS.DASHBOARD.CHARTS.STATUS_DISTRIBUTION}>
              <SectionHead
                title="Distribución por estado"
                subtitle={`${formatNumber(statusTotal, 0)} documentos en el período`}
              />
              {/*
                La distribucion son 7 consultas en paralelo (una por estado mas
                el total), asi que tarda mas que el resto del dashboard: sin este
                estado de carga se veia vacio y parecia "cero facturas".
              */}
              {statusLoading ? (
                <div className="mt-5 flex h-[184px] items-center justify-center">
                  <Spinner size="md" />
                </div>
              ) : statusData.length > 0 ? (
                <div className="mt-4">
                  <BreakdownDonutChart data={statusData} valueLabel="Facturas" centerLabel="DOCUMENTOS" />
                </div>
              ) : (
                <div className="mt-5 flex h-[184px] items-center justify-center text-sm text-secondary-400">
                  Sin facturas en el período
                </div>
              )}
            </div>

            {/* Tipos de Documentos — antes era una tarjeta chica del grid
                de KPIs; se promovió acá para ir al lado de Distribución
                por estado con el mismo peso visual y el mismo gráfico. */}
            <div className={`${CARD_SHELL} p-6`} data-testid={TEST_IDS.DASHBOARD.STATS.DOCUMENT_TYPES}>
              <SectionHead
                title="Tipos de Documentos"
                subtitle={`${formatNumber(metrics?.document_types ?? 0, 0)} tipos diferentes · Top 5 por cantidad`}
              />
              {rankingsLoading ? (
                <div className="mt-5 flex h-[184px] items-center justify-center">
                  <Spinner size="md" />
                </div>
              ) : docTypeBreakdown.length > 0 ? (
                <div className="mt-4">
                  <BreakdownDonutChart
                    data={docTypeBreakdown.map((d) => ({ name: d.name, value: d.quantity, color: d.color }))}
                    valueLabel="Documentos"
                    centerLabel="DOCUMENTOS"
                  />
                </div>
              ) : (
                <div className="mt-5 flex h-[184px] items-center justify-center text-sm text-secondary-400">
                  Sin datos para el período
                </div>
              )}
            </div>
          </div>

          {/* Facturación mensual — a todo el ancho, debajo. Gate propio: esta
              sí depende de monthlyData/chartsLoading (es la única que grafica
              esa serie), pero ahora ya no le tapa la vista a las otras dos. */}
          <div className={`${CARD_SHELL} p-6`} data-testid={TEST_IDS.DASHBOARD.CHARTS.MONTHLY}>
            <SectionHead
              title="Facturación mensual"
              subtitle={`Últimos 6 meses · ${monthlyMetric === 'amount' ? `valores en ${CURRENCY}` : 'cantidad de documentos'}`}
              right={
                <div className="inline-flex shrink-0 rounded-lg border border-[#ececec] bg-secondary-50 p-0.5 text-[12px] font-semibold dark:border-secondary-700 dark:bg-secondary-900">
                  {(['amount', 'invoices'] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setMonthlyMetric(m)}
                      className={
                        monthlyMetric === m
                          ? 'rounded-[5px] bg-white px-2.5 py-1.5 text-ink-850 shadow-sm dark:bg-secondary-700 dark:text-white'
                          : 'rounded-[5px] px-2.5 py-1.5 text-secondary-500'
                      }
                    >
                      {m === 'amount' ? 'Monto' : 'Cantidad'}
                    </button>
                  ))}
                </div>
              }
            />
            {chartsLoading ? (
              <div className="mt-6 flex h-[260px] items-center justify-center">
                <Spinner size="md" />
              </div>
            ) : monthlyData.length > 0 ? (
              <div className="mt-6">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={monthlyData} margin={{ top: 20, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke="#f0f0f0" />
                    <XAxis
                      dataKey="month"
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: '#94a3b8', fontSize: 11, fontWeight: 600 }}
                    />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      width={56}
                      tick={{ fill: '#94a3b8', fontSize: 11 }}
                    />
                    <Tooltip
                      cursor={{ fill: 'rgba(10,34,51,.04)' }}
                      contentStyle={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #ececec',
                        borderRadius: '10px',
                        boxShadow: '0 8px 20px -10px rgba(10,34,51,.2)',
                        fontSize: 12,
                      }}
                      labelStyle={{ color: '#0a2233', fontWeight: 700 }}
                      formatter={(value: any) => [
                        monthlyMetric === 'amount'
                          ? `${CURRENCY} ${formatNumber(Number(value))}`
                          : formatNumber(Number(value), 0),
                        monthlyMetric === 'amount' ? 'Monto' : 'Facturas',
                      ]}
                    />
                    {/*
                      isAnimationActive={false}: con React 18 en StrictMode
                      (doble montaje) recharts 2.15 deja los <g> de las barras
                      VACIOS, sin el <path> adentro — el grafico no dibujaba
                      nada. Bug preexistente, no introducido por el rediseño.
                    */}
                    <Bar
                      dataKey={monthlyMetric}
                      radius={[6, 6, 0, 0]}
                      maxBarSize={64}
                      isAnimationActive={false}
                    >
                      {/*
                        El ultimo mes se destaca, como en el diseño. Violeta de
                        marca en vez del gris: en gris las barras de los meses
                        anteriores se leian como "deshabilitadas".
                      */}
                      {monthlyData.map((_, i) => (
                        <Cell key={i} fill={i === monthlyData.length - 1 ? '#6b239e' : '#b98ad6'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="mt-5 flex h-[260px] items-center justify-center text-sm text-secondary-400">
                Sin datos mensuales para el período
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
